import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import Lottie from 'lottie-react';
import styles from './ProgressModal.module.css';
import patientService from '../../services/PatientService';

const ProgressModal = ({ isOpen, onClose, patientId }) => {
  const progressChartRef = useRef();
  const [animationComplete, setAnimationComplete] = useState(false);
  const [bronzeAnimation, setBronzeAnimation] = useState(null);
  const [dateRange, setDateRange] = useState('7d'); // '7d', '30d', 'all'
  const [metric, setMetric] = useState('sessions'); // 'sessions' or 'reps'
  const [loading, setLoading] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Auto-determine grouping based on date range
  const getGrouping = (range) => {
    if (range === '7d') return 'daily';   // 7 bars
    if (range === '30d') return 'daily';  // 30 bars
    return 'monthly';                      // ~12 bars for a year
  };

  // Get human-readable labels
  const getRangeLabel = (range) => {
    if (range === '7d') return 'Last 7 Days';
    if (range === '30d') return 'Last 30 Days';
    return 'All Time';
  };

  useEffect(() => {
    // Only handle cleanup when modal closes - chart creation is handled by progressData useEffect
    if (!isOpen && progressChartRef.current) {
      d3.select(progressChartRef.current).selectAll('*').remove();
      setAnimationComplete(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const loadStreakAnimation = async () => {
      try {
        const response = await fetch('/movemendstreak.json');
        if (response.ok) {
          const animationData = await response.json();
          setBronzeAnimation(animationData);
        } else {
          console.log('Streak animation file not found');
        }
      } catch (error) {
        console.log('Could not load streak animation:', error);
      }
    };

    loadStreakAnimation();

    // Handle responsive resize
    let resizeTimeout;
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      
      // Debounce chart redraw on resize
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (progressData && progressChartRef.current) {
          createProgressChart();
        }
      }, 250);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [progressData, dateRange, metric]);


  // Fetch patient data when modal opens or filters change
  useEffect(() => {
    if (isOpen && patientId) {
      fetchPatientData();
    }
  }, [isOpen, patientId, dateRange]);

  // Trigger chart redraw when data changes
  useEffect(() => {
    if (isOpen && progressData && progressChartRef.current) {
      // Small delay to ensure container is properly sized
      const timeoutId = setTimeout(() => {
        createProgressChart();
        setAnimationComplete(true);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, progressData, dateRange, metric]);

  const fetchPatientData = async () => {
    setLoading(true);
    try {
      const analytics = await patientService.getPatientAnalytics(patientId, dateRange);
      
      console.log('📊 Analytics received:', analytics);
      
      if (analytics && analytics.sessions && analytics.sessions.length > 0) {
        setHasData(true);
        const processedData = processAnalyticsData(analytics);
        setProgressData(processedData);
      } else {
        setHasData(false);
        setProgressData(null);
      }
    } catch (error) {
      console.error('Error fetching patient progress data:', error);
      setHasData(false);
      setProgressData(null);
    } finally {
      setLoading(false);
    }
  };

  const processAnalyticsData = (analytics) => {
    const sessions = analytics.sessions || [];
    
    // Calculate achievements
    const totalSessions = sessions.length;
    const totalTimeSeconds = sessions.reduce((sum, s) => sum + (s.duration_seconds || 0), 0);
    const totalReps = sessions.reduce((sum, s) => sum + (s.reps_completed || 0), 0);
    const bestReps = sessions.length > 0 ? Math.max(...sessions.map(s => s.reps_completed || 0)) : 0;
    
    // Calculate streak (consecutive days with sessions)
    const streak = calculateStreak(sessions);
    
    // Group sessions by date for chart (auto-determined)
    const grouping = getGrouping(dateRange);
    const groupedSessions = groupSessionsByPeriod(sessions, grouping);
    
    return {
      achievements: {
        totalWorkouts: totalSessions,
        totalTimeSeconds: totalTimeSeconds, // Store as seconds for flexible formatting
        movementsCompleted: totalReps,
        personalBest: bestReps,
        currentStreak: streak
      },
      chartData: groupedSessions
    };
  };

  const calculateStreak = (sessions) => {
    if (!sessions || sessions.length === 0) return 0;
    
    // Get unique dates (YYYY-MM-DD format)
    const dates = [...new Set(sessions.map(s => 
      new Date(s.start_time).toISOString().split('T')[0]
    ))].sort().reverse();
    
    if (dates.length === 0) return 0;
    
    // Check if today or yesterday has a session
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    
    if (dates[0] !== today && dates[0] !== yesterday) {
      return 0; // Streak broken
    }
    
    // Count consecutive days
    let streak = 1;
    const startDate = new Date(dates[0]);
    
    for (let i = 1; i < dates.length; i++) {
      const expectedDate = new Date(startDate - i * 86400000).toISOString().split('T')[0];
      if (dates[i] === expectedDate) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };

  const groupSessionsByPeriod = (sessions, grouping) => {
    if (!sessions || sessions.length === 0) return [];
    
    const grouped = {};
    
    sessions.forEach(session => {
      const date = new Date(session.start_time);
      let key, label;
      
      if (grouping === 'daily') {
        key = date.toISOString().split('T')[0];
        label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else if (grouping === 'weekly') {
        // Group by week (starting Monday)
        const weekStart = new Date(date);
        const day = weekStart.getDay();
        const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
        weekStart.setDate(diff);
        key = weekStart.toISOString().split('T')[0];
        label = `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      } else { // monthly
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
      
      if (!grouped[key]) {
        grouped[key] = { key, label, count: 0, reps: 0 };
      }
      grouped[key].count++;
      grouped[key].reps += session.reps_completed || 0;
    });
    
    // Convert to array and sort by date
    return Object.values(grouped).sort((a, b) => a.key.localeCompare(b.key));
  };

  const createProgressChart = () => {
    const container = progressChartRef.current;
    if (!container) return;
    
    // Clean up any existing chart and event listeners
    d3.select(container).selectAll('*').remove();

    // Use real data or show empty state
    const chartData = progressData?.chartData || [];
    
    if (chartData.length === 0) {
      // Show empty state message in chart area
      d3.select(container)
        .append("div")
        .style("display", "flex")
        .style("align-items", "center")
        .style("justify-content", "center")
        .style("height", "100%")
        .style("color", "#6D4F79")
        .style("font-family", '"Scope One", serif')
        .style("font-size", "16px")
        .style("opacity", "0.6")
        .text("No activity data yet. Start playing games to see your progress!");
      return;
    }

    const containerRect = container.getBoundingClientRect();
    // Responsive margins - smaller on mobile
    const margin = isMobile 
      ? { top: 40, right: 15, bottom: 35, left: 25 }
      : { top: 50, right: 20, bottom: 40, left: 30 };
    const width = containerRect.width - margin.left - margin.right;
    const height = Math.max(containerRect.height - margin.top - margin.bottom, 200);

    const svg = d3.select(container)
      .append("svg")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("background", "#FFFFED")
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Create theme-colored gradient based on selected metric
    const defs = svg.append("defs");
    
    const color = metric === 'sessions' ? '#6D4F79' : '#4A9B8E';
    
    const gradient = defs.append("linearGradient")
      .attr("id", "metricGradient")
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", 0).attr("y1", height)
      .attr("x2", 0).attr("y2", 0);

    gradient.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", color)
      .attr("stop-opacity", 0.3);

    gradient.append("stop")
      .attr("offset", "100%")
      .attr("stop-color", color)
      .attr("stop-opacity", 0.9);

    // Glow filter
    const filter = defs.append("filter")
      .attr("id", "glow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");

    filter.append("feGaussianBlur")
      .attr("stdDeviation", "3")
      .attr("result", "coloredBlur");

    const feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "coloredBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");

    // Smart padding based on data density
    let barPadding = 0.25;
    const dataPoints = chartData.length;
    
    if (dataPoints === 1) {
      barPadding = 0.8;
    } else if (dataPoints <= 3) {
      barPadding = 0.6;
    } else if (dataPoints <= 7) {
      barPadding = 0.4;
    } else if (dataPoints > 20) {
      barPadding = 0.15;
    }

    // Scales
    const xScale = d3.scaleBand()
      .domain(chartData.map(d => d.label))
      .range([0, width])
      .padding(barPadding);

    // Y Scale based on selected metric
    const getValue = (d) => metric === 'sessions' ? d.count : d.reps;
    const maxValue = d3.max(chartData, getValue) || 1;
    const yDomain = maxValue <= 5 ? 5 : maxValue * 1.1;
    
    const yScale = d3.scaleLinear()
      .domain([0, yDomain])
      .range([height, 0]);

    // Create bars
    const bars = svg.selectAll(".metric-bar")
      .data(chartData)
      .enter()
      .append("rect")
      .attr("class", "metric-bar")
      .attr("x", d => xScale(d.label))
      .attr("width", xScale.bandwidth())
      .attr("y", height)
      .attr("height", 0)
      .attr("fill", "url(#metricGradient)")
      .attr("rx", 8)
      .style("filter", "url(#glow)")
      .style("cursor", "pointer");

    // Staggered bounce animation
    bars.transition()
      .delay((d, i) => i * 150)
      .duration(1000)
      .ease(d3.easeBounceOut)
      .attr("y", d => yScale(getValue(d)))
      .attr("height", d => height - yScale(getValue(d)));

    // Add floating value labels
    const labels = svg.selectAll(".metric-label")
      .data(chartData)
      .enter()
      .append("text")
      .attr("class", "metric-label")
      .attr("x", d => xScale(d.label) + xScale.bandwidth() / 2)
      .attr("y", d => yScale(getValue(d)) - 8)
      .attr("text-anchor", "middle")
      .style("fill", color)
      .style("font-size", isMobile ? "11px" : "13px")
      .style("font-weight", "700")
      .style("opacity", 0)
      .text(d => getValue(d));

    // Typewriter effect for labels
    labels.transition()
      .delay((d, i) => i * 150 + 1000)
      .duration(600)
      .style("opacity", 1)
      .attr("y", d => yScale(getValue(d)) - 12);

    // Add date labels with slide-in effect
    // Smart label formatting based on data density (best practice)
    const truncateLabel = (label, maxLength) => {
      if (label.length <= maxLength) return label;
      return label.substring(0, maxLength - 1) + '…';
    };
    
    // Determine label length based on number of data points and screen size
    let maxLabelLength = 15; // Default
    if (isMobile) {
      maxLabelLength = dataPoints > 15 ? 6 : 8;
    } else if (dataPoints > 30) {
      maxLabelLength = 8;
    } else if (dataPoints > 15) {
      maxLabelLength = 10;
    } else if (dataPoints > 10) maxLabelLength = 10; // Medium
    // else keep full labels for sparse data
    
    const dateLabels = svg.selectAll(".date-label")
      .data(chartData)
      .enter()
      .append("text")
      .attr("class", "date-label")
      .attr("x", d => xScale(d.label) + xScale.bandwidth() / 2)
      .attr("y", height + 25)
      .attr("text-anchor", "middle")
      .style("fill", "#6D4F79")
      .style("font-size", isMobile ? "9px" : "11px")
      .style("font-weight", "600")
      .style("opacity", 0)
      .attr("transform", "translate(0, 10)")
      .text(d => truncateLabel(d.label, maxLabelLength));

    dateLabels.transition()
      .delay((d, i) => i * 100 + 500)
      .duration(500)
      .style("opacity", 1)
      .attr("transform", "translate(0, 0)");

    // Create tooltip for additional context (best practice)
    const tooltip = d3.select(container)
      .append("div")
      .style("position", "absolute")
      .style("background", "#6D4F79")
      .style("color", "#FFFFED")
      .style("padding", "8px 12px")
      .style("border-radius", "8px")
      .style("font-family", '"Scope One", serif')
      .style("font-size", "12px")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("z-index", "1000")
      .style("box-shadow", "0 2px 8px rgba(0,0,0,0.2)");

    // Add interactive hover effects
    bars.on("mouseenter", function(event, d) {
      const val = getValue(d);
      d3.select(this)
        .transition()
        .duration(200)
        .attr("rx", 12)
        .style("filter", "url(#glow) brightness(1.15)")
        .attr("y", yScale(val) - 5)
        .attr("height", height - yScale(val) + 5);
      
      // Highlight corresponding label
      svg.selectAll(".metric-label")
        .filter(labelData => labelData.label === d.label)
        .transition()
        .duration(200)
        .style("font-size", isMobile ? "13px" : "15px")
        .attr("y", yScale(val) - 18);
      
      // Show tooltip
      const text = metric === 'sessions' 
        ? `${val} ${val === 1 ? 'session' : 'sessions'}` 
        : `${val} ${val === 1 ? 'rep' : 'reps'}`;
      tooltip
        .html(`<strong>${d.label}</strong><br/>${text}`)
        .style("opacity", 0.95)
        .style("left", (event.pageX - container.getBoundingClientRect().left + 10) + "px")
        .style("top", (event.pageY - container.getBoundingClientRect().top - 10) + "px");
        
    }).on("mousemove", function(event, d) {
      tooltip
        .style("left", (event.pageX - container.getBoundingClientRect().left + 10) + "px")
        .style("top", (event.pageY - container.getBoundingClientRect().top - 10) + "px");
        
    }).on("mouseleave", function(event, d) {
      const val = getValue(d);
      d3.select(this)
        .transition()
        .duration(200)
        .attr("rx", 8)
        .style("filter", "url(#glow)")
        .attr("y", yScale(val))
        .attr("height", height - yScale(val));
      
      svg.selectAll(".metric-label")
        .filter(labelData => labelData.label === d.label)
        .transition()
        .duration(200)
        .style("font-size", isMobile ? "11px" : "13px")
        .attr("y", yScale(val) - 12);
      
      tooltip.style("opacity", 0);
    });

    // Add animated title with fade-in effect
    const metricLabel = metric === 'sessions' ? 'Sessions' : 'Reps';
    const titleText = `Your ${getRangeLabel(dateRange)} - ${metricLabel}`;
    
    svg.append("text")
      .attr("x", width / 2)
      .attr("y", -20)
      .attr("text-anchor", "middle")
      .style("fill", color)
      .style("font-size", isMobile ? "14px" : "16px")
      .style("font-weight", "700")
      .style("opacity", 0)
      .style("font-family", '"Scope One", serif')
      .text(titleText)
      .transition()
      .delay(1800)
      .duration(800)
      .style("opacity", 1);

    // Add sparkle animation on bars
    chartData.forEach((d, i) => {
      setTimeout(() => {
        const sparkle = svg.append("circle")
          .attr("cx", xScale(d.label) + xScale.bandwidth() / 2)
          .attr("cy", yScale(getValue(d)) - 10)
          .attr("r", 0)
          .style("fill", metric === 'sessions' ? "#B8A5C9" : "#7FD4C1")
          .style("opacity", 0.8);

        sparkle.transition()
          .duration(400)
          .attr("r", 4)
          .transition()
          .duration(400)
          .attr("r", 0)
          .style("opacity", 0)
          .remove();
      }, i * 150 + 1200);
    });
  };

  const StreakBadge = ({ streakCount }) => {
    return (
      <div className={styles.streakBadge}>
        <div className={styles.badgeContainer} style={{ 
          position: 'relative', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: '100%',
          height: 'auto'
        }}>
          <Lottie 
            animationData={bronzeAnimation}
            loop={true}
            autoplay={true}
            style={{ 
              width: 380, 
              height: 380,
              margin: '0 auto'
            }}
          />
        </div>
        <div className={styles.streakText}>
          <div className={styles.streakTitle}>{streakCount} Day Exercise Streak</div>
          <div className={styles.streakSubtitle}>Keep it going!</div>
        </div>
      </div>
    );
  };

  const formatTime = (totalSeconds) => {
    if (totalSeconds < 60) {
      // Show seconds
      return totalSeconds === 1 ? "1 second" : `${totalSeconds} seconds`;
    } else if (totalSeconds < 3600) {
      // Show minutes (less than 1 hour)
      const minutes = Math.floor(totalSeconds / 60);
      return minutes === 1 ? "1 minute" : `${minutes} minutes`;
    } else {
      // Show hours
      const hours = Math.floor(totalSeconds / 3600);
      return hours === 1 ? "1 hour" : `${hours} hours`;
    }
  };

  if (!isOpen) return null;

  // Get data or show empty state
  const achievements = progressData?.achievements || {
    totalWorkouts: 0,
    totalTimeSeconds: 0,
    movementsCompleted: 0,
    personalBest: 0,
    currentStreak: 0
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h1 className={styles.modalTitle}>Look What You've Accomplished!</h1>
          <button className={styles.closeButton} onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M6 18L18 6" stroke="#6D4F79" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className={styles.mainContent}>
          {/* Controls Row */}
          <div className={styles.controlsRow}>
            {/* Metric Toggle */}
            <div className={styles.metricToggle}>
              <span className={styles.showLabel}>Show:</span>
              <button
                onClick={() => setMetric('sessions')}
                className={styles.controlButton}
                style={{
                  borderColor: '#6D4F79',
                  background: metric === 'sessions' ? '#6D4F79' : 'transparent',
                  color: metric === 'sessions' ? '#FFFFED' : '#6D4F79',
                  boxShadow: metric === 'sessions' ? '0 2px 8px rgba(109, 79, 121, 0.3)' : 'none'
                }}
              >Sessions</button>
              <button
                onClick={() => setMetric('reps')}
                className={styles.controlButton}
                style={{
                  borderColor: '#4A9B8E',
                  background: metric === 'reps' ? '#4A9B8E' : 'transparent',
                  color: metric === 'reps' ? '#FFFFED' : '#4A9B8E',
                  boxShadow: metric === 'reps' ? '0 2px 8px rgba(74, 155, 142, 0.3)' : 'none'
                }}
              >Reps</button>
            </div>

            {/* Time Range Buttons */}
            <div className={styles.timeRangeButtons}>
              <button
                onClick={() => setDateRange('7d')}
                className={styles.controlButton}
                style={{
                  borderColor: '#6D4F79',
                  background: dateRange === '7d' ? '#6D4F79' : 'transparent',
                  color: dateRange === '7d' ? '#FFFFED' : '#6D4F79',
                  boxShadow: dateRange === '7d' ? '0 2px 8px rgba(109, 79, 121, 0.3)' : 'none'
                }}
              >{isMobile ? '7 Days' : 'Last 7 Days'}</button>
              <button
                onClick={() => setDateRange('30d')}
                className={styles.controlButton}
                style={{
                  borderColor: '#6D4F79',
                  background: dateRange === '30d' ? '#6D4F79' : 'transparent',
                  color: dateRange === '30d' ? '#FFFFED' : '#6D4F79',
                  boxShadow: dateRange === '30d' ? '0 2px 8px rgba(109, 79, 121, 0.3)' : 'none'
                }}
              >{isMobile ? '30 Days' : 'Last 30 Days'}</button>
              <button
                onClick={() => setDateRange('all')}
                className={styles.controlButton}
                style={{
                  borderColor: '#6D4F79',
                  background: dateRange === 'all' ? '#6D4F79' : 'transparent',
                  color: dateRange === 'all' ? '#FFFFED' : '#6D4F79',
                  boxShadow: dateRange === 'all' ? '0 2px 8px rgba(109, 79, 121, 0.3)' : 'none'
                }}
              >All Time</button>
            </div>
          </div>

          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              color: '#6D4F79',
              fontFamily: '"Scope One", serif',
              fontSize: '18px'
            }}>
              Loading your progress...
            </div>
          ) : !hasData ? (
            /* Beautiful Empty State */
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '64px',
                marginBottom: '1rem'
              }}>🌟</div>
              <h2 style={{
                color: '#6D4F79',
                fontFamily: '"Scope One", serif',
                fontSize: '24px',
                fontWeight: '600',
                marginBottom: '1rem'
              }}>Your Journey Begins Here!</h2>
              <p style={{
                color: '#6D4F79',
                fontFamily: '"Scope One", serif',
                fontSize: '16px',
                maxWidth: '500px',
                lineHeight: '1.6',
                opacity: 0.8
              }}>
                Start playing therapeutic games to see your amazing progress here. 
                Every session counts towards building healthier habits and reaching your goals!
              </p>
            </div>
          ) : (
            <>
              {/* Celebration Stats */}
              <div className={styles.celebrationSection}>
                <div className={styles.achievementGrid}>
                  <div className={styles.achievementCard}>
                    <div className={styles.achievementNumber}>{achievements.totalWorkouts.toLocaleString()}</div>
                    <div className={styles.achievementLabel}>Total number of sessions</div>
                    <div className={styles.achievementMessage}>That's incredible dedication!</div>
                  </div>
                  <div className={styles.achievementCard}>
                    <div className={styles.achievementNumber}>{formatTime(achievements.totalTimeSeconds)}</div>
                    <div className={styles.achievementLabel}>Total time played</div>
                    <div className={styles.achievementMessage}>Investing in your health!</div>
                  </div>
                  <div className={styles.achievementCard}>
                    <div className={styles.achievementNumber}>{achievements.movementsCompleted.toLocaleString()}</div>
                    <div className={styles.achievementLabel}>Total Reps</div>
                    <div className={styles.achievementMessage}>Every rep counts!</div>
                  </div>
                  <div className={styles.achievementCard}>
                    <div className={styles.achievementNumber}>{achievements.personalBest}</div>
                    <div className={styles.achievementLabel}>Best reps in a session</div>
                    <div className={styles.achievementMessage}>You're getting stronger!</div>
                  </div>
                </div>
              </div>

              {/* Progress Chart and Streak */}
              <div className={styles.progressAndStreakSection}>
                <div className={styles.chartSection}>
                  <div ref={progressChartRef} className={styles.chartContainer}></div>
                </div>
                
                <div className={styles.sideInfo}>
                  <div className={styles.streakSection}>
                    <StreakBadge streakCount={achievements.currentStreak} />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgressModal; 